import { useState } from "react";
import { ChevronDown, Search, Zap } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import type { PartialSkill } from "~/services/skills";
import { SkillsList } from "./SkillsList";

interface SkillsPopoverProps {
  skills: PartialSkill[];
  // Receives the slash-prefixed token (e.g. "/kaplan-meier") ready to insert
  // into the composer.
  onSelect: (skillToken: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skill: PartialSkill) => void;
  onDeleteSkill?: (skill: PartialSkill) => void;
  onViewSkill?: (skill: PartialSkill) => void;
  datasetNames?: Record<string, string>;
  onLearnMore?: () => void;
}

export const SkillsPopover = ({
  skills,
  onSelect,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
  onViewSkill,
  datasetNames,
  onLearnMore,
}: SkillsPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const handleSelect = (skillName: string) => {
    onSelect(`/${skillName}`);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs text-muted-foreground"
        >
          <Zap className="size-3.5" />
          Skills
          <ChevronDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-96 flex-col overflow-hidden p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="shrink-0 border-b px-3 py-2">
          <p className="text-sm font-semibold">Skills</p>
          <p className="text-xs text-muted-foreground">Insert a skill into your message.</p>
        </div>
        <div className="shrink-0 border-b px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search skills…"
              className="h-8 pl-7 text-xs"
              aria-label="Search skills"
            />
          </div>
        </div>

        <SkillsList
          skills={skills}
          searchFilter={query}
          onSelectSkill={handleSelect}
          onAddSkill={onAddSkill}
          onEditSkill={onEditSkill}
          onDeleteSkill={onDeleteSkill}
          onViewSkill={onViewSkill}
          datasetNames={datasetNames}
          onLearnMore={onLearnMore}
          onClose={() => setOpen(false)}
          fill
        />
      </PopoverContent>
    </Popover>
  );
};
